<script lang="ts" setup>
import placeholder from '~/assets/icons/placeholder.svg?url';
import type { ProductType } from '~/utils/api/common';

const props = defineProps<{
  product: ProductType;
  refilBadge?: boolean;
  dataTestId?: string;
  image?: { lazy?: boolean };
  newTab?: boolean;
}>();

// Product cards inside articles open the product page in a new tab.
const linkTarget = computed(() => (props.newTab ? '_blank' : undefined));
const emit = defineEmits<{
  (event: 'add', productId: number): void;
}>();

const { t } = useI18n();

const stringPrice = computed(() => priceFormat(props.product.priceInfo.price));
const stringPrevPrice = computed(
  () => props.product.priceInfo.basePrice && priceFormat(props.product.priceInfo.basePrice),
);
const saleBadge = computed(
  () =>
    props.product.priceInfo.basePrice &&
    Math.round((1 - props.product.priceInfo.price / props.product.priceInfo.basePrice) * 100),
);
</script>

<template>
  <div class="productCard" :data-test-id="dataTestId">
    <div
      class="productCard__container"
      :class="{
        productCard__container_notAvailable: !product.addToBasketId,
      }"
    >
      <NuxtLink :to="`/product/${product.code}`" :target="linkTarget" :rel="newTab ? 'noopener' : undefined">
        <NuxtImg
          format="webp"
          :src="product.picture || placeholder"
          :alt="product.altPicture || product.name"
          :sizes="IMAGE_SIZES.mobileHalfScreen"
          :placeholder="placeholder"
          :loading="image?.lazy ? 'lazy' : undefined"
          class="productCard__image"
        />

        <span class="productCard__notAvailableText" v-if="!product.addToBasketId && !product.previewMode">
          {{ t('no_stock') }}
        </span>
      </NuxtLink>

      <ClientOnly v-if="product.addToBasketId && !product.previewMode">
        <div class="productCard__actions">
          <ProductCardActions
            @add="emit('add', product.id)"
            :product="product"
            :data-test-id="dataTestId && `${dataTestId}-actions`"
          />
        </div>
      </ClientOnly>
    </div>

    <NuxtLink :to="`/product/${product.code}`" :target="linkTarget" :rel="newTab ? 'noopener' : undefined">
      <p class="productCard__name">
        {{ product.name }}
      </p>
      <span v-if="product.volume" class="productCard__value">
        {{ product.volume }}
      </span>
      <span v-if="!product.previewMode" class="productCard__price">
        {{ stringPrice }}

        <span v-if="stringPrevPrice" class="productCard__price_prev">
          {{ stringPrevPrice }}
        </span>
      </span>
    </NuxtLink>

    <div class="productCard__badges">
      <span v-if="product.isTwoPlusOne" class="productCard__badge productCard__badge_twoPlusOne">
        {{ t('promo_label') }}
      </span>
      <span v-if="product.isNew" class="productCard__badge productCard__badge_new">
        {{ t('new_badge') }}
      </span>
      <span v-if="saleBadge" class="productCard__badge productCard__badge_sale">
        {{ t('sale_badge', { value: saleBadge }) }}
      </span>
      <span v-if="refilBadge" class="productCard__badge productCard__badge_refil">
        {{ t('refil_badge') }}
      </span>
    </div>
  </div>
</template>

<i18n>
ru:
  no_stock: Нет в наличии
  new_badge: Новинка
  sale_badge: '{value}%'
  refil_badge: -30% по подписке
  # Название акции — не переводится, ключ нужен только чтобы обойти no-raw-text
  promo_label: '2=3'

en:
  no_stock: Not available
  new_badge: New
  sale_badge: '{value}%'
  refil_badge: -30% on subscription
  promo_label: '2=3'
</i18n>

<style lang="scss">
.productCard {
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.2px;
  color: #121212;
  position: relative;
  display: block;

  @include mq('sm') {
    font-size: 14px;
    line-height: 20px;
  }

  a {
    display: block;
    color: inherit;
  }

  &__container {
    position: relative;
    overflow: hidden;

    &_notAvailable {
      opacity: 0.5;
    }
  }

  &__notAvailableText {
    display: flex;
    width: 140px;
    min-width: fit-content;
    height: 37px;
    padding: 4px 6px;
    justify-content: center;
    align-items: center;
    position: absolute;
    left: 16px;
    top: 18px;
    background: rgba(0, 0, 0, 0.2);
    backdrop-filter: blur(2.5px);

    color: #fff;
    font-size: 14px;
    line-height: 12px;
    letter-spacing: 0.25px;
    text-transform: uppercase;

    @include mq('sm') {
      left: 10px;
      top: 11px;
      width: 78px;
      height: 20px;
      font-size: 8px;
    }
  }

  &__badges {
    position: absolute;
    left: 50%;
    top: 0;
    transform: translate(-50%, -50%);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    pointer-events: none;

    @include mq('sm') {
      gap: 4px;
    }
  }

  &__badge {
    width: 90px;
    height: 40px;
    border-radius: 20px;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    flex-shrink: 0;

    &_new {
      background-color: #8ab844;
    }

    &_twoPlusOne {
      // Размер/шрифт — как у «Новинки» (базовый 90×40/16px), отличается только цвет.
      background-color: #ff9d6c;
    }

    &_sale {
      background-color: #121212;
    }

    &_refil {
      width: 190px;
      height: 52px;
      border-radius: 50px;
      background-color: #8ab844;

      @include mq('sm') {
        width: 150px;
        height: 40px;
      }
    }
  }

  &__image {
    aspect-ratio: 338 / 444;
    object-fit: cover;
    display: block;
    width: 100%;
    overflow-wrap: anywhere;
  }

  &__name {
    padding-top: 12px;
    margin: 0;
  }

  &__value {
    display: block;
    padding-top: 2px;
  }

  &__price {
    display: flex;
    padding-top: 2px;
    gap: 16px;

    @include mq('sm') {
      gap: 12px;
    }

    &_prev {
      opacity: 0.4;
      text-decoration-line: line-through;
    }
  }

  &__actions {
    position: absolute;
    transform: translateY(100%);
    padding: 19px 14px 17px;
    transition: transform 0.5s ease;
    bottom: 0;
    width: 100%;
    background-color: #fff;
    border: 1px solid #f2f2f2;

    @include mq('sm') {
      padding: 12px;
    }
  }

  @media (hover: hover) {
    &:hover &__actions {
      transform: translateY(0);
    }
  }

  &__desktopText {
    display: inline-block;

    @include mq('sm') {
      display: none;
    }
  }
}
</style>
